import { useEffect, useState } from "react";
import className from "classnames/bind";
import { Banner, CarouselShop, GridInductees } from "@components";
import { DigitalExhibitContentFragment } from "@graphqlTypes";
import { getReduceMotion } from "@/utils/reduceMotion";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
import { accentVar } from "../utils";

import styles from "./DigitalExhibitPageEnder.module.scss";
const cx = className.bind(styles);

type PageEnderProps = NonNullable<
  DigitalExhibitContentFragment["digitalExhibitContent"]
>["pageEnder"];

// Two identical halves so the -50% marquee translate loops seamlessly
const TICKER_REPEATS = 8;

// Page Ender — locked "EXPLORE MORE" marquee ticker followed by
// editor-configured core-site modules.
export default function DigitalExhibitPageEnder(props: PageEnderProps) {
  const { tickerAccent, showCta, cta, showInductees, inductees, showShop, shop } = props || {};
  const { labels } = usePageWrapperData();
  const exploreMore = labels?.exploreMore || "Explore More";
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    setReduceMotion(getReduceMotion());
  }, []);

  // The ticker is a breaker INTO the modules below it — with every section
  // toggled off there is nothing to introduce, so the whole ender hides
  const hasSections = Boolean((showCta && cta) || (showInductees && inductees) || (showShop && shop));
  if (!hasSections) return null;

  return (
    <section
      className={cx("dx-page-ender")}
      style={{ "--highlight-color": accentVar(tickerAccent) } as React.CSSProperties}
    >
      {/* Ticker copy from Global Labels; items alternate white/accent with white arrows per the comp */}
      <div className={cx("dx-page-ender__ticker")} aria-hidden="true">
        <div
          className={cx("dx-page-ender__ticker-track", { "dx-page-ender__ticker-track--static": reduceMotion })}
        >
          {Array.from({ length: TICKER_REPEATS }, (_, i) => (
            <span
              key={i}
              className={cx("dx-page-ender__ticker-item", { "dx-page-ender__ticker-item--accent": i % 2 === 1 })}
            >
              <span className={cx("dx-page-ender__ticker-text")}>{exploreMore}</span>
              {/* Comp uses the ↓ glyph in the ticker font (not an icon) so it
                  matches the cap height + weight of the text */}
              <span className={cx("dx-page-ender__ticker-arrow")}>↓</span>
            </span>
          ))}
        </div>
      </div>
      {showCta && cta && <Banner {...cta} />}
      {/* Grid layout per the comp; hand-picked inductees pass through as nodes.
          Cloned field shapes match the components at runtime, hence the casts */}
      {showInductees && inductees && (
        <GridInductees
          colorOverride={inductees.colorOverride as any}
          headline={inductees.headline}
          cta={inductees.cta as any}
          inductees={inductees.featuredInductees?.nodes as any}
        />
      )}
      {showShop && shop && <CarouselShop {...(shop as any)} />}
    </section>
  );
}
